import { ChatAltIcon } from "@heroicons/react/outline";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/solid";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  ActionLabel,
  CoachingDashboardItemNode,
  CoachingDimensionNode,
  CoachingEvidenceNode,
  CoachingInsightNode,
} from "types/graphql-schema";

import AppLink from "@components/link/link";

import { DimensionIcon, StatusBadge } from "./coaching-components";
import { priorityBadge } from "./coaching-constants";
import type { Priority, Status } from "./coaching-constants";

const ACTION_LABEL_DISPLAY: Record<ActionLabel, string> = {
  [ActionLabel.KeepDoing]: "Keep Doing",
  [ActionLabel.ManagerAction]: "Manager Action",
  [ActionLabel.OpenNextMeetingWith]: "Open Next Meeting With",
  [ActionLabel.SuggestedDiscussionTopic]: "Suggested Discussion Topic",
};

export type ExpandOverride = { expanded: boolean; version: number } | null;

const InsightCard = ({
  insight,
  expandOverride,
}: {
  insight: CoachingInsightNode;
  expandOverride: ExpandOverride;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (expandOverride) {
      setIsOpen(expandOverride.expanded);
    }
  }, [expandOverride?.version]);
  const badge = priorityBadge[(insight.priority ?? "low") as Priority];

  const filteredEvidence = (insight.evidence ?? []).filter(
    (ev): ev is CoachingEvidenceNode => ev != null,
  );

  const uniqueSources = filteredEvidence
    .filter((ev) => ev.sourceUrl)
    .reduce((acc, ev) => {
      if (!acc.some((s) => s.sourceUrl === ev.sourceUrl)) {
        acc.push(ev);
      }
      return acc;
    }, [] as CoachingEvidenceNode[]);

  return (
    <div className="rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 overflow-hidden flex flex-col mb-1 p-2">
      <button
        type="button"
        className="px-2 py-2 flex items-center justify-between gap-3 cursor-pointer w-full text-left rounded-md hover:bg-gray-100 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <h4 className="tf-header-sm-dark">{insight.title}</h4>
        <div className="flex items-center gap-2.5 shrink-0">
          {badge && <StatusBadge status={badge.status} label={badge.label} />}
          {isOpen ? (
            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-2 pb-2 pt-2 flex flex-col gap-5">
              {/* Context section */}
              <div className="flex flex-col gap-1">
                <h5 className="tf-header-sm">Context</h5>
                <p className="tf-body-sm-light">{insight.body}</p>
                {filteredEvidence.length > 0 && (
                  <div className="flex flex-col gap-3 mt-2">
                    {filteredEvidence.map((ev, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <ChatAltIcon className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
                        <p className="tf-body-sm-light">{ev.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Action section */}
              {insight.action && (
                <div className="flex flex-col gap-1">
                  <h5 className="tf-header-sm">
                    {insight.action.label
                      ? (ACTION_LABEL_DISPLAY[
                          insight.action.label as ActionLabel
                        ] ?? insight.action.label)
                      : "Action"}
                  </h5>
                  <p className="tf-body-sm-light">{insight.action.text}</p>
                </div>
              )}

              {/* Sources section */}
              {uniqueSources.length > 0 && (
                <div className="flex flex-col gap-1">
                  <h5 className="tf-header-sm">Sources</h5>
                  <ul className="flex flex-col gap-1">
                    {uniqueSources.map((ev, i) => (
                      <li key={i} className="text-sm">
                        <AppLink
                          to={ev.sourceUrl!}
                          className="text-blue-600 hover:text-blue-700 hover:underline"
                        >
                          {ev.sourceTitle || "View source"}
                        </AppLink>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const DimensionSection = ({
  dimension,
  dashboardCards,
  expandOverride,
}: {
  dimension: CoachingDimensionNode;
  dashboardCards: CoachingDashboardItemNode[];
  expandOverride: ExpandOverride;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (expandOverride) {
      setIsOpen(expandOverride.expanded);
    }
  }, [expandOverride?.version]);

  const dashboardDetail = dashboardCards.find(
    (card) => card.dimension === dimension.name,
  )?.detail;

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        className="flex flex-col gap-0.5 px-4 py-3 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between gap-3 w-full">
          <div className="flex items-center gap-2.5">
            <DimensionIcon
              name={dimension.name ?? ""}
              className="h-4 w-4 text-gray-500"
            />
            <h3 className="tf-header-base-dark">{dimension.name}</h3>
          </div>
          <div className="flex items-center gap-2.5">
            <StatusBadge
              status={(dimension.status ?? "good") as Status}
              label={dimension.status ?? ""}
            />
            {isOpen ? (
              <ChevronDownIcon className="h-4 w-4 text-gray-400 shrink-0" />
            ) : (
              <ChevronRightIcon className="h-4 w-4 text-gray-400 shrink-0" />
            )}
          </div>
        </div>
        {dashboardDetail && (
          <p className="pl-[26px] text-sm text-gray-500 text-left">
            {dashboardDetail}
          </p>
        )}
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <div className="flex flex-col gap-2 pl-10 mb-9 pt-1">
              {(dimension.insights ?? [])
                .filter(
                  (insight): insight is CoachingInsightNode => insight != null,
                )
                .sort((a, b) => {
                  const order: Record<string, number> = {
                    high: 0,
                    medium: 1,
                    low: 2,
                    positive: 3,
                  };
                  return (
                    (order[a.priority ?? "positive"] ?? 4) -
                    (order[b.priority ?? "positive"] ?? 4)
                  );
                })
                .map((insight, i) => (
                  <InsightCard
                    key={insight.title ?? i}
                    insight={insight}
                    expandOverride={expandOverride}
                  />
                ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
